import { useEffect, useState } from "react";

interface ImageLightboxProps {
  images: string[];
  index: number;
  title: string;
  onClose: () => void;
  onChange: (index: number) => void;
}

export default function ImageLightbox({ images, index, title, onClose, onChange }: ImageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onChange((index + 1) % images.length);
      if (event.key === "ArrowLeft") onChange((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [index, images.length, onClose, onChange]);

  useEffect(() => {
    setZoomed(false);
  }, [index]);

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/95">
      <div className="flex items-center justify-between px-4 py-4 md:px-8">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-white">{title}</span>
          <span className="text-xs text-white/60">
            {index + 1} / {images.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setZoomed((prev) => !prev)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-white/30 px-3 py-1.5 text-xs text-white transition-colors hover:bg-white/10"
          >
            <i className={zoomed ? "ri-zoom-out-line" : "ri-zoom-in-line"} />
            {zoomed ? "缩小" : "放大"}
          </button>
          <button
            type="button"
            aria-label="关闭预览"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-4">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => onChange((index - 1 + images.length) % images.length)}
          className="absolute left-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10 md:left-6 md:h-12 md:w-12"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <img
          src={images[index]}
          alt={`${title} 大图预览 ${index + 1}`}
          title={`${title} 大图预览`}
          onClick={() => setZoomed((prev) => !prev)}
          className={`max-h-full max-w-full cursor-zoom-in object-contain transition-transform duration-300 ${
            zoomed ? "scale-150 cursor-zoom-out" : "scale-100"
          }`}
        />
        <button
          type="button"
          aria-label="下一张"
          onClick={() => onChange((index + 1) % images.length)}
          className="absolute right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10 md:right-6 md:h-12 md:w-12"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 overflow-x-auto px-4 py-4 no-scrollbar">
        {images.map((image, i) => (
          <button
            key={`${image}-${i}`}
            type="button"
            onClick={() => onChange(i)}
            className={`h-14 w-12 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${
              i === index ? "border-primary-400" : "border-transparent opacity-60 hover:opacity-100"
            }`}
          >
            <img src={image} alt={`缩略图 ${i + 1}`} className="h-full w-full object-cover object-top" />
          </button>
        ))}
      </div>
    </div>
  );
}